// packages/engine/src/process-bpmn.ts
//
// Horizontal-BPMN placement over a ProcessScaffold (plan
// 2026-07-07-process-scaffold-bpmn-diff P2). Main axis x = document order,
// cross axis y = branch tracks — NO identity lanes: a chain is always a
// straight line, branches band below their split, and the serpentine /
// lane-collision machinery of the swimlane flow has no counterpart here.
//
// Construct mapping (locked in the plan):
//   if            → XOR gateway pair, branch labels on split out-edges
//   select        → OR gateway pair; `select N` → merge labelled "N of M"
//   parallel      → AND gateway pair; merge labelled with the mergeAspect
//   each          → loop frame around the body band
//   try/catch     → error-boundary events heading catch bands + XOR merge
//   do            → collapsed subprocess task | embedded subprocess frame
//   await         → start event when a trigger, else intermediate event
//   go            → link event + dashed re-entry back-edge
//   inbound call  → message-catch styling on the task
//   fail          → error end; finish → end
//   finish all    → terminate end (no compensation — PROCESS.md §10)
//   fail all      → cancel end (compensation-triggering — §10)
//   reversible    → forward steps on the spine; unwind steps + the saga's
//                   fail divert to a compensation band (dashed chain) with
//                   failure-drop edges from the walker's CompensationFrames
//   note/prose    → annotation nodes; dist step runs → dash-dot group frame

import type { NodeId } from "./process-walker-types.js";
import { isAwaitNodeId } from "./process-walker-types.js";
import type { QualifiedName } from "./types.js";
import type {
  ProcessScaffold,
  ScaffoldBranch,
  ScaffoldDiffTag,
  ScaffoldNode,
  ScaffoldStep,
} from "./process-scaffold-types.js";
import type {
  BpmnEdge,
  BpmnFrame,
  BpmnLayout,
  BpmnNode,
  BpmnOwner,
  DanglingEngine,
  GatewayVariant,
} from "./process-bpmn-types.js";

/**
 * Split/merge gateway variants for a parallel container, decided from the
 * walker's mergeAspect grammar: `[out] [join|race|select] [N|boolean]`, or
 * `await <head>` (the boundary-timeout sugar — race is implied).
 *   race (first-wins) / await → event-based split (deferred choice), XOR converge
 *   join N / race N / select N / boolean → complex merge (N-of-M semantics)
 *   parallel select → inclusive split (concurrent inclusive)
 *   bare parallel / bare join / out → plain AND pair
 */
function parallelVariants(aspect: string | undefined): {
  split: GatewayVariant;
  merge: GatewayVariant;
} {
  if (!aspect) return { split: "and", merge: "and" };
  const toks = aspect.split(/\s+/);
  let i = 0;
  if (toks[i] === "out") i++;
  const head = toks[i];
  const rest = toks.slice(i + 1).join(" ");
  if (head === "await") return { split: "event", merge: "xor" };
  if (head === "race") {
    if (!rest || rest === "1") return { split: "event", merge: "xor" };
    return { split: "and", merge: "complex" };
  }
  if (head === "select") return { split: "or", merge: rest ? "complex" : "or" };
  if (head === "join") return { split: "and", merge: rest ? "complex" : "and" };
  return { split: "and", merge: "and" }; // "out" alone
}

function branchedVariants(
  sub: "if" | "switch" | "try" | "parallel",
  mergeAspect: string | undefined,
  runCount: number | undefined,
): { split: GatewayVariant; merge: GatewayVariant } {
  if (sub === "if" || sub === "try") return { split: "xor", merge: "xor" };
  if (sub === "switch") return { split: "or", merge: runCount !== undefined ? "complex" : "or" };
  return parallelVariants(mergeAspect);
}

/** `await` steps are detectable by the emitter's nodeId kind segment —
 *  keying on it avoids false positives from a note reading "await …". */
function isAwaitStep(s: ScaffoldStep): boolean {
  return isAwaitNodeId(s.nodeId);
}

/** Duration (`1 day`, `30 min`) or cron (`0 3 1 * *`) head → timer glyph;
 *  any other free-form head reads as a condition (PROCESS.md §6). */
const DURATION_RE =
  /^\d+\s*(ms|msecs?|s|secs?|seconds?|m|mins?|minutes?|h|hrs?|hours?|d|days?|w|weeks?|months?|y|years?)$/i;
const CRON_RE = /^[\d*/,-]+(\s+[\d*/,-]+){4,5}$/;
function awaitTrigger(head: string | undefined): "timer" | "conditional" {
  const h = (head ?? "").trim();
  return DURATION_RE.test(h) || CRON_RE.test(h) ? "timer" : "conditional";
}

/** Control owner (each/branched prefix) → BpmnOwner. */
function controlOwnerOf(n: {
  owner?: readonly string[];
  ownerIsActor?: boolean;
}): BpmnOwner | undefined {
  const path = n.owner;
  if (!path || path.length === 0) return undefined;
  return {
    label: path[path.length - 1] ?? "",
    isActor: n.ownerIsActor ?? false,
    path: path as BpmnOwner["path"],
  };
}

function ownerOf(s: ScaffoldStep): BpmnOwner | undefined {
  const path = s.fromIsActor ? s.from : s.fromModule ?? s.from;
  if (path.length === 0) return undefined;
  return {
    label: s.fromInstance ?? path[path.length - 1] ?? "",
    isActor: s.fromIsActor,
    path,
    ...(s.fromInstance !== undefined ? { instance: s.fromInstance } : {}),
  };
}

/** First step/terminator nodeId in a subtree — used to sniff emitter path
 *  markers (`/fanout/`) that classify a construct. */
function firstNodeIdIn(nodes: readonly ScaffoldNode[]): string | null {
  for (const n of nodes) {
    if (n.kind === "step" || n.kind === "terminator") return n.nodeId;
    if (n.kind === "each" || (n.kind === "do" && n.body)) {
      const r = firstNodeIdIn(n.kind === "each" ? n.children : n.body!);
      if (r) return r;
    }
    if (n.kind === "branched") {
      for (const g of n.groups) {
        const r = firstNodeIdIn(g.children);
        if (r) return r;
      }
    }
  }
  return null;
}

/** An exit awaiting connection to the next element; `label`, the default-flow
 *  marker, and the pickable-branch handle ride the edge. */
interface Src {
  readonly id: string;
  readonly label?: string;
  readonly isDefault?: boolean;
  readonly branchOf?: { readonly frameId: NodeId; readonly label: string };
}

/** Walker-generated `branch:N` names are positional, not authored — never
 *  show them as edge labels. */
function displayLabel(label: string | undefined): string | undefined {
  if (!label || /^branch:\d+$/.test(label)) return undefined;
  return label;
}

/**
 * `bpmnLayout` options. `replicate` turns on the per-step system-cylinder
 * projection (aspect render mode `replicated`): each step whose callee module
 * carries the named aspect plane gets a private duplicate of the place node
 * drawn beside it (a `system` node + short `assoc` stub), instead of the
 * shared board `web` hub. Render-only; truth (aspect membership) is unchanged.
 */
export interface BpmnLayoutOptions {
  readonly replicate?: {
    /** Aspect plane to replicate (e.g. `system`) — for docs/diagnostics. */
    readonly key: string;
    /** Callee module path → its place on the plane, or null when it carries
     *  none. `logicalId` is the place module's stable key (shared by every
     *  copy); `value` is the display label; `color` is the resolved tint
     *  (from the caller's `aspectColorFor`, so board + process agree). */
    readonly placeOf: (
      calleeModule: QualifiedName,
    ) => { readonly logicalId: string; readonly value: string; readonly color: string } | null;
  };
  /**
   * `engine @@<key>` — the caller-derived counterpart of `replicate`: each
   * call-step task node gets a dangling aspect badge resolved from its CALLER
   * module (not the callee), attached to the node itself (no separate grid
   * node). Render-only; truth (aspect membership) is unchanged.
   */
  readonly engine?: {
    /** Aspect plane (e.g. `team`) — for docs/diagnostics. */
    readonly key: string;
    /** Caller module path → its aspect place, or null when it carries none
     *  (or the caller is an actor). Shape mirrors {@link DanglingEngine}. */
    readonly placeOf: (
      callerModule: QualifiedName,
    ) => { readonly logicalId: string; readonly value: string; readonly color: string } | null;
  };
}

export function bpmnLayout(scaffold: ProcessScaffold, opts: BpmnLayoutOptions = {}): BpmnLayout {
  const nodes: BpmnNode[] = [];
  const frames: BpmnFrame[] = [];
  const edges: BpmnEdge[] = [];
  const byStepIndex = new Map<number, string>();
  const triggerSet = new Set<NodeId>(scaffold.triggers);
  // Compensation nodes divert out of the main flow, grouped per saga in
  // encounter order (= the emitter's reverse-item order = fire order).
  const comp = new Map<NodeId, ScaffoldNode[]>();
  let maxX = 0;
  let distSeq = 0;

  function addNode(n: BpmnNode): void {
    nodes.push(n);
    if (n.x + 1 > maxX) maxX = n.x + 1;
    const si = "stepIndex" in n ? n.stepIndex ?? null : null;
    if (si !== null) byStepIndex.set(si, n.id);
  }

  /** `engine @@<key>` — spread the caller-derived aspect badge onto a task,
   *  or nothing when disabled / the caller is an actor / carries no value. */
  function engineFor(fromModule: QualifiedName | null): { danglingEngine?: DanglingEngine } {
    if (!opts.engine || !fromModule) return {};
    const p = opts.engine.placeOf(fromModule);
    if (!p) return {};
    return {
      danglingEngine: { key: opts.engine.key, value: p.value, color: p.color, logicalId: p.logicalId },
    };
  }

  function connect(pending: readonly Src[], toId: string): Src[] {
    for (const s of pending) {
      const label = displayLabel(s.label);
      edges.push({
        from: s.id,
        to: toId,
        style: "seq",
        ...(label ? { label } : {}),
        ...(s.isDefault ? { isDefault: true } : {}),
        ...(s.branchOf ? { branchOf: s.branchOf } : {}),
      });
    }
    return [{ id: toId }];
  }

  /**
   * Lay a body band: elements left→right starting at x0, spine on `track`,
   * nested containers may spread onto tracks below. Returns the column after
   * the band, the band's track height, and the exits awaiting connection.
   */
  function layBand(
    items: readonly ScaffoldNode[],
    x0: number,
    track: number,
    pendingIn: readonly Src[],
  ): { endX: number; height: number; pending: Src[] } {
    let x = x0;
    let height = 1;
    let pending: Src[] = [...pendingIn];
    // dist run tracking (steps only; any other element breaks the run).
    let distStartX = -1;
    let distEndX = -1;
    function flushDist() {
      if (distStartX >= 0) {
        frames.push({
          id: `#dist:${distSeq++}`,
          kind: "dist",
          label: "dist",
          x0: distStartX,
          y0: track,
          x1: distEndX,
          y1: track,
        });
        distStartX = -1;
        distEndX = -1;
      }
    }

    for (const n of items) {
      // Saga compensation diverts to its band (steps + the saga's fail).
      if (n.kind === "step" && n.modifiers.compensation && n.modifiers.reversibleId) {
        const list = comp.get(n.modifiers.reversibleId) ?? [];
        list.push(n);
        comp.set(n.modifiers.reversibleId, list);
        continue;
      }
      if (n.kind === "terminator" && n.comp) {
        const list = comp.get(n.comp.reversibleId) ?? [];
        list.push(n);
        comp.set(n.comp.reversibleId, list);
        continue;
      }

      if (n.kind === "step") {
        if (n.modifiers.collapsedSubprocessGroup) continue;
        if (n.modifiers.dist) {
          if (distStartX < 0) distStartX = x;
          distEndX = x;
        } else flushDist();

        if (n.prose || (n.noteText !== undefined && !isAwaitStep(n) && !n.reentryTarget)) {
          // Note / prose → annotation. Kept IN the sequence (order matters to
          // the reader) but associated, not flow-connected: the incoming edge
          // is an association and pending passes through it.
          addNode({
            kind: "annotation",
            id: n.nodeId,
            x,
            y: track,
            text: n.noteText ?? "",
            ...(n.diff ? { diff: n.diff } : {}),
            stepIndex: n.stepIndex,
            ...(n.prose ? { prose: true } : {}),
            ...(n.prose ? {} : { owner: ownerOf(n) }),
            ...(n.modifiers.branchSelected === false ? { dimmed: true } : {}),
          });
          for (const s of pending) edges.push({ from: s.id, to: n.nodeId, style: "assoc" });
          x++;
        } else if (isAwaitStep(n)) {
          const isTrigger = triggerSet.has(n.nodeId);
          const head = n.noteText?.replace(/^await /, "");
          addNode({
            kind: "event",
            id: n.nodeId,
            x,
            y: track,
            event: isTrigger ? "start" : "intermediate",
            trigger: awaitTrigger(head),
            label: head,
            stepIndex: n.stepIndex,
            owner: ownerOf(n),
            ...(n.modifiers.branchSelected === false ? { dimmed: true } : {}),
            ...(n.diff ? { diff: n.diff } : {}),
          });
          pending = connect(pending, n.nodeId);
          x++;
        } else if (n.reentryTarget) {
          addNode({
            kind: "event",
            id: n.nodeId,
            x,
            y: track,
            event: "link",
            label: n.noteText,
            stepIndex: n.stepIndex,
            owner: ownerOf(n),
            ...(n.modifiers.branchSelected === false ? { dimmed: true } : {}),
            ...(n.diff ? { diff: n.diff } : {}),
          });
          connect(pending, n.nodeId);
          pending = []; // a `go` is a jump — nothing flows forward out of the link
          // Back-edge to the `as` step it resumes at (resolved reentries only).
          const re = scaffold.reentries.find((r) => r.fromNodeId === n.nodeId);
          if (re?.toNodeId) edges.push({ from: n.nodeId, to: re.toNodeId, style: "reentry" });
          x++;
        } else {
          addNode({
            kind: "task",
            id: n.nodeId,
            x,
            y: track,
            stepIndex: n.stepIndex,
            label: n.to.join("."),
            owner: ownerOf(n),
            ...(n.direction === "inbound" ? { inbound: true } : {}),
            ...(n.modifiers.branchSelected === false ? { dimmed: true } : {}),
            ...engineFor(n.fromModule),
            ...(n.diff ? { diff: n.diff } : {}),
          });
          // `replicated` aspect mode: a per-step system cylinder beside the task,
          // half a track below (its own compacted rank once lanes are assigned),
          // in the step's swimlane (shares its owner), joined by a short stub.
          const place = opts.replicate?.placeOf(n.toModule) ?? null;
          if (place) {
            const sysId = `${n.nodeId}::sys`;
            addNode({
              kind: "system",
              id: sysId,
              x,
              y: track + 0.5,
              label: place.value,
              logicalId: place.logicalId,
              accentValue: place.value,
              accent: place.color,
              owner: ownerOf(n),
              ...(n.modifiers.branchSelected === false ? { dimmed: true } : {}),
              ...(n.diff ? { diff: n.diff } : {}),
            });
            edges.push({ from: n.nodeId, to: sysId, style: "assoc" });
          }
          pending = connect(pending, n.nodeId);
          x++;
        }
      } else if (n.kind === "terminator") {
        flushDist();
        const event =
          n.type === "finish"
            ? n.all
              ? "end-terminate"
              : "end"
            : n.all
              ? "end-cancel"
              : "end-error";
        addNode({
          kind: "event",
          id: n.nodeId,
          x,
          y: track,
          event,
          ...(n.reason ? { label: n.reason } : {}),
          ...(n.diff ? { diff: n.diff } : {}),
        });
        connect(pending, n.nodeId);
        pending = []; // nothing flows out of an end event
        x++;
      } else if (n.kind === "each") {
        flushDist();
        const bodyStart = x;
        const r = layBand(n.children, x, track, pending);
        const x1 = Math.max(r.endX - 1, bodyStart);
        frames.push({
          id: n.nodeId,
          kind: "each",
          owner: controlOwnerOf(n),
          marker: n.iterates ? "multi" : "loop",
          label: n.label,
          ...(n.diff ? { diff: n.diff } : {}),
          x0: bodyStart,
          y0: track,
          x1,
          y1: track + r.height - 1,
        });
        pending = r.pending;
        x = Math.max(r.endX, bodyStart + 1);
        height = Math.max(height, r.height);
      } else if (n.kind === "do") {
        flushDist();
        if (!n.expanded) {
          addNode({
            kind: "task",
            id: n.nodeId,
            x,
            y: track,
            stepIndex: null,
            label: n.label,
            subprocess: { nodeId: n.nodeId, expanded: false },
            ...(n.diff ? { diff: n.diff } : {}),
          });
          pending = connect(pending, n.nodeId);
          x++;
        } else {
          const bodyStart = x;
          const r = layBand(n.body ?? [], x, track, pending);
          const x1 = Math.max(r.endX - 1, bodyStart);
          frames.push({
            id: n.nodeId,
            kind: "do",
            label: n.label,
            ...(n.diff ? { diff: n.diff } : {}),
            x0: bodyStart,
            y0: track,
            x1,
            y1: track + r.height - 1,
          });
          pending = r.pending;
          x = Math.max(r.endX, bodyStart + 1);
          height = Math.max(height, r.height);
        }
      } else if (n.kind === "branched" && n.sub === "try") {
        flushDist();
        const [tryGroup, ...catches] = n.groups;
        const tryStart = x;
        const tryRes = layBand(tryGroup?.children ?? [], x, track, pending);
        const tryX1 = Math.max(tryRes.endX - 1, tryStart);
        frames.push({
          id: n.nodeId,
          kind: "try",
          owner: controlOwnerOf(n),
          label: n.label,
          ...(n.diff ? { diff: n.diff } : {}),
          x0: tryStart,
          y0: track,
          x1: tryX1,
          y1: track + tryRes.height - 1,
        });
        let catchTrack = track + tryRes.height;
        let mergeX = Math.max(tryRes.endX, tryStart + 1);
        const exits: Src[] = [...tryRes.pending];
        catches.forEach((c, i) => {
          const bId = `${n.nodeId}#boundary:${i}`;
          addNode({
            kind: "event",
            id: bId,
            x: tryStart,
            y: catchTrack,
            event: "boundary-error",
            owner: controlOwnerOf(n),
            ...(c.label ? { label: c.label } : {}),
            attachedTo: n.nodeId,
          });
          // Attachment association: frame → its boundary event.
          edges.push({ from: n.nodeId, to: bId, style: "assoc" });
          const r = layBand(c.children, tryStart + 1, catchTrack, [{ id: bId }]);
          exits.push(...r.pending);
          if (r.endX > mergeX) mergeX = r.endX;
          catchTrack += r.height;
        });
        const mergeId = `${n.nodeId}#merge`;
        addNode({
          kind: "gateway",
          id: mergeId,
          x: mergeX,
          y: track,
          gate: "merge",
          variant: "xor",
          frameId: n.nodeId,
          frameKind: "try",
          owner: controlOwnerOf(n),
          ...(n.diff ? { diff: n.diff } : {}),
        });
        pending = connect(exits, mergeId);
        x = mergeX + 1;
        height = Math.max(height, catchTrack - track);
      } else if (n.kind === "branched") {
        flushDist();

        // `parallel … each …` fan-out arrives as one branch labelled "each"
        // (emitter carve-out; child nodeIds under `/fanout/`). BPMN models a
        // fan-out as a parallel MULTI-INSTANCE activity (||| marker), not a
        // gateway pair.
        const fanout =
          n.sub === "parallel" &&
          n.groups.length === 1 &&
          n.groups[0]!.label === "each" &&
          n.nodeId.length > 0 &&
          firstNodeIdIn(n.groups[0]!.children)?.includes("/fanout/") === true;
        if (fanout) {
          const bodyStart = x;
          const r = layBand(n.groups[0]!.children, x, track, pending);
          const x1 = Math.max(r.endX - 1, bodyStart);
          frames.push({
            id: n.nodeId,
            kind: "each",
            owner: controlOwnerOf(n),
            marker: "multi-parallel",
            label: `each${n.mergeAspect ? ` (${n.mergeAspect})` : ""}`,
            ...(n.diff ? { diff: n.diff } : {}),
            x0: bodyStart,
            y0: track,
            x1,
            y1: track + r.height - 1,
          });
          pending = r.pending;
          x = Math.max(r.endX, bodyStart + 1);
          height = Math.max(height, r.height);
          continue;
        }

        const variants = branchedVariants(n.sub, n.mergeAspect, n.runCount);
        // `parallel out` detaches: no merge, no wait — the main flow continues
        // straight from the split and the detached branches dangle below into
        // their own end events (fire-and-forget, per BPMN reading).
        const detached = n.sub === "parallel" && /^out\b/.test(n.mergeAspect ?? "");
        const pickable = n.sub !== "parallel";
        const splitId = `${n.nodeId}#split`;
        addNode({
          kind: "gateway",
          id: splitId,
          x,
          y: track,
          gate: "split",
          variant: variants.split,
          frameId: n.nodeId,
          frameKind: n.sub,
          owner: controlOwnerOf(n),
          label: detached ? `${n.label} — ${n.mergeAspect}` : n.label,
          ...(detached ? { detached: true } : {}),
          ...(n.diff ? { diff: n.diff } : {}),
        });
        pending = connect(pending, splitId);

        // Detached branches keep the spine track free for the continuation.
        let branchTrack = detached ? track + 1 : track;
        let mergeX = x + 1;
        const exits: Src[] = [];
        const emptyBranches: { label: string; isElse?: boolean }[] = [];
        for (const b of n.groups) {
          if (b.children.length === 0) {
            emptyBranches.push({ label: b.label, ...(b.isElse ? { isElse: true } : {}) });
            branchTrack += 1;
            continue;
          }
          const src: Src = {
            id: splitId,
            label: b.label,
            ...(b.isElse ? { isDefault: true } : {}),
            ...(pickable ? { branchOf: { frameId: n.nodeId, label: b.label } } : {}),
          };
          const r = layBand(b.children, x + 1, branchTrack, [src]);
          if (detached) {
            // Dangling exits get their own implicit end events.
            for (const ex of r.pending) {
              const endId = `${ex.id}#detached-end`;
              addNode({ kind: "event", id: endId, x: r.endX, y: branchTrack, event: "end", owner: controlOwnerOf(n) });
              connect([ex], endId);
            }
            if (r.endX + 1 > mergeX) mergeX = r.endX + 1;
          } else {
            exits.push(...r.pending);
            if (r.endX > mergeX) mergeX = r.endX;
          }
          branchTrack += r.height;
        }

        if (detached) {
          // Continuation flows straight out of the split; nothing merges back.
          pending = [{ id: splitId }];
          x = x + 1;
          height = Math.max(height, branchTrack - track);
          continue;
        }

        const mergeId = `${n.nodeId}#merge`;
        const mergeLabel =
          n.sub === "switch" && n.runCount !== undefined
            ? `${n.runCount} of ${n.groups.length}`
            : n.mergeAspect;
        addNode({
          kind: "gateway",
          id: mergeId,
          x: mergeX,
          y: track,
          gate: "merge",
          variant: variants.merge,
          frameId: n.nodeId,
          frameKind: n.sub,
          owner: controlOwnerOf(n),
          ...(mergeLabel ? { label: mergeLabel } : {}),
          ...(n.diff ? { diff: n.diff } : {}),
        });
        for (const eb of emptyBranches) {
          const lbl = displayLabel(eb.label);
          edges.push({
            from: splitId,
            to: mergeId,
            style: "seq",
            ...(lbl ? { label: lbl } : {}),
            ...(eb.isElse ? { isDefault: true } : {}),
            ...(pickable ? { branchOf: { frameId: n.nodeId, label: eb.label } } : {}),
          });
        }
        pending = connect(exits, mergeId);
        if (emptyBranches.length && exits.length === 0) pending = [{ id: mergeId }];
        x = mergeX + 1;
        height = Math.max(height, branchTrack - track);
      }
    }
    flushDist();
    return { endX: x, height, pending };
  }

  // ── Root ────────────────────────────────────────────────────────────────
  const items = [...scaffold.tree];
  const leadingTriggers: ScaffoldStep[] = [];
  while (
    items.length &&
    items[0]!.kind === "step" &&
    isAwaitStep(items[0] as ScaffoldStep) &&
    triggerSet.has((items[0] as ScaffoldStep).nodeId)
  ) {
    leadingTriggers.push(items.shift() as ScaffoldStep);
  }
  // A multi-await head (PROCESS.md §6) is already unwrapped to stacked trigger
  // awaits by the scaffold (unwrapMultiAwaitStart), so the loop above collects
  // them — both backends render multiple-start from one truth, no per-backend
  // race-gateway unwrap here.

  let pending: Src[] = [];
  if (leadingTriggers.length) {
    leadingTriggers.forEach((t, i) => {
      const head = t.noteText?.replace(/^await /, "");
      addNode({
        kind: "event",
        id: t.nodeId,
        x: 0,
        y: i,
        event: "start",
        trigger: awaitTrigger(head),
        label: head,
        stepIndex: t.stepIndex,
        owner: ownerOf(t),
      });
      pending.push({ id: t.nodeId });
    });
  } else {
    addNode({ kind: "event", id: "#start", x: 0, y: 0, event: "start" });
    pending = [{ id: "#start" }];
  }

  const root = layBand(items, 1, 0, pending);
  let height = Math.max(root.height, leadingTriggers.length || 1);

  // Fall-off-the-end completion: a run that doesn't terminate gets an end event.
  if (root.pending.length) {
    const endId = "#end";
    addNode({ kind: "event", id: endId, x: root.endX, y: 0, event: "end" });
    connect(root.pending, endId);
  }

  // ── Compensation bands (one per saga, below the main flow) ─────────────
  // Each unwind pairs with its forward step through the emitter's nodeId
  // structure (`…/reversible:N/item:RI/…` ↔ `…/reversible:N/unwind:RI/…`),
  // so the compensation activity sits DIRECTLY UNDER the step it undoes and
  // the backward chain reads right→left in fire order, ending at the saga's
  // default fail on the left — mirroring the flow view's backward run.
  for (const list of comp.values()) {
    const track = height;
    // Union scaffolds namespace before-only ids with `before:` — a removed
    // unwind must still pair with its MATCHED forward step (which kept the
    // after id), so pairing keys are prefix-normalized on both sides.
    const normalize = (id: string) => (id.startsWith("before:") ? id.slice("before:".length) : id);
    const forwardX = new Map<string, number>(); // "<revPath>/item:RI" → x
    for (const nd of nodes) {
      const m = normalize(nd.id).match(/^(.*\/reversible:\d+)\/item:(\d+)\//);
      if (m && !forwardX.has(`${m[1]}/item:${m[2]}`)) {
        forwardX.set(`${m[1]}/item:${m[2]}`, nd.x);
      }
    }
    const occupied = new Set<number>();
    const place = (desired: number): number => {
      let d = Math.max(0, desired);
      while (occupied.has(d)) d++;
      occupied.add(d);
      return d;
    };
    let prevId: string | null = null;
    let prevX = Number.POSITIVE_INFINITY;
    let minX = Number.POSITIVE_INFINITY;
    for (const n of list) {
      if (n.kind !== "step" && n.kind !== "terminator") continue;
      const um = normalize(n.nodeId).match(/^(.*\/reversible:\d+)\/unwind:(\d+)\//);
      const paired = um ? forwardX.get(`${um[1]}/item:${um[2]}`) : undefined;
      // Terminators (the saga's default fail) close the chain LEFT of the
      // last unwind; unpaired entries fall back beside the previous one.
      const desired =
        n.kind === "terminator"
          ? (minX === Number.POSITIVE_INFINITY ? 1 : minX - 1)
          : paired ?? (prevX === Number.POSITIVE_INFINITY ? 1 : prevX + 1);
      const px = place(desired);
      if (n.kind === "step") {
        addNode({
          kind: "task",
          id: n.nodeId,
          x: px,
          y: track,
          stepIndex: n.stepIndex,
          label: n.noteText ?? n.to.join("."),
          owner: ownerOf(n),
          compensation: true,
          ...engineFor(n.fromModule),
          ...(n.diff ? { diff: n.diff } : {}),
        });
      } else {
        addNode({
          kind: "event",
          id: n.nodeId,
          x: px,
          y: track,
          event: n.type === "finish" ? "end" : "end-error",
          ...(n.reason ? { label: n.reason } : {}),
          compensation: true,
          ...(n.diff ? { diff: n.diff } : {}),
        });
      }
      if (prevId) edges.push({ from: prevId, to: n.nodeId, style: "comp" });
      prevId = n.nodeId;
      prevX = px;
      if (px < minX) minX = px;
    }
    height = track + 1;
  }

  // Failure drops: forward step fails → control drops into a compensation
  // entry (the walker resolved which). Only edges whose ends both rendered.
  const rendered = new Set(nodes.map((n) => n.id));
  for (const frame of scaffold.compensation) {
    for (const d of frame.drops) {
      if (rendered.has(d.fromNodeId) && rendered.has(d.toNodeId)) {
        edges.push({ from: d.fromNodeId, to: d.toNodeId, style: "drop" });
      }
    }
  }
  // Re-entry back-edges get the same guard — a union scaffold may carry a
  // reentry whose target did not survive; never draw onto a stale id.
  const guarded = edges.filter(
    (e) => e.style !== "reentry" || (rendered.has(e.from) && rendered.has(e.to)),
  );

  // Edge-level diff. The scaffold has no edge identity (edges synthesize from
  // union adjacency above), so a seq edge's diff derives from its endpoints:
  // both added/both removed → the edge belongs to that change (an inserted
  // chain's internal edges are added, a removed run's are removed). `changed`
  // never propagates — the change belongs to the node, not the flow.
  const diffById = new Map<string, ScaffoldDiffTag | undefined>();
  for (const n of nodes) diffById.set(n.id, n.diff);
  const tagged = guarded.map((e) => {
    if (e.style !== "seq") return e;
    const a = diffById.get(e.from)?.status;
    const b = diffById.get(e.to)?.status;
    return a && a === b && (a === "added" || a === "removed") ? { ...e, diff: { status: a } } : e;
  });

  return { nodes, frames, edges: tagged, width: maxX, height, byStepIndex, lanes: [], pools: [] };
}
